import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, alpha, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import {
  getSupplyChainOverview, getSupplyChainAuditChain, ingestSbomToSupplyChain, attestSupplyChainArtifact,
  signSupplyChainArtifact, verifySupplyChainArtifact, verifySupplyChainAttestation
} from 'api/securityOps';
import { GREEN, MONO, ago } from './scanUi';
import { STAGES, counts, orderRows, stageOf, verdict } from './supplyLogic';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const VERDICT_COLOR = { ok: GREEN, bad: '#ef4444', gone: '#94a3b8' };
const SBOM_START = '{\n  "bomFormat": "CycloneDX",\n  "specVersion": "1.5",\n  "components": []\n}';
const fail = (e, fb) => e?.response?.data?.detail || e?.message || fb;

const Step = ({ label, on }) => (
  <Stack direction="row" spacing={0.8} alignItems="center">
    <Box sx={{ width: 10, height: 10, borderRadius: '50%', border: 2, borderColor: on ? GREEN : 'divider', bgcolor: on ? GREEN : 'transparent' }} />
    <Typography variant="caption" color={on ? 'text.primary' : 'text.secondary'}>{label}</Typography>
  </Stack>
);

export default function SupplyChainPage() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const [checks, setChecks] = useState({});
  const [sbom, setSbom] = useState(null);

  const load = useCallback(async () => {
    try {
      const [d, a] = await Promise.all([getSupplyChainOverview({ limit: 200 }), getSupplyChainAuditChain({ limit: 200 }).catch(() => null)]);
      setRows(d?.results || []);
      setSummary(d?.summary || {});
      setAudit(a);
    } catch (e) { setError(fail(e, 'Could not load your files.')); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (row, fn) => {
    setBusy(row.id); setError('');
    try { await fn(); await load(); } catch (e) { setError(fail(e, 'That did not work.')); } finally { setBusy(null); }
  };
  const sign = (r) => run(r, () => signSupplyChainArtifact(r.id));
  const attest = (r) => run(r, () => attestSupplyChainArtifact({ artifact: r.id, predicate_type: 'slsa_provenance', predicate: { builder: 'fuzzscan', source: 'scan-report' } }));
  const check = (r) => run(r, async () => {
    const sig = await verifySupplyChainArtifact(r.id);
    const att = r.is_attested ? await verifySupplyChainAttestation({ artifact: r.id }) : null;
    setChecks((c) => ({ ...c, [r.id]: { ...verdict(sig, att), at: new Date().toISOString() } }));
  });
  const importSbom = async () => {
    setError('');
    try {
      await ingestSbomToSupplyChain({ scan: sbom.scan, name: sbom.name, sbom: JSON.parse(sbom.json) });
      setSbom(null); await load();
    } catch (e) { setError(e instanceof SyntaxError ? 'That is not valid JSON.' : fail(e, 'Could not import the list.')); }
  };

  const c = useMemo(() => counts(rows), [rows]);
  const list = useMemo(() => orderRows(rows), [rows]);
  const act = (r) => {
    const s = stageOf(r);
    const go = (fn, label, variant = 'contained') => <Button size="small" variant={variant} color={variant === 'contained' ? 'success' : 'inherit'} disabled={busy === r.id} onClick={() => fn(r)}>{label}</Button>;
    if (s === 'none') return go(sign, STAGES.none.next);
    if (s === 'signed') return go(attest, STAGES.signed.next);
    if (s === 'good') return go(check, STAGES.good.next, 'outlined');
    return <Typography variant="caption" color="text.secondary">Nothing to check</Typography>;
  };
  const stat = (label, value, color, sub) => (
    <Box sx={{ ...panel, p: 2.2, flex: '1 1 190px' }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 700, fontFamily: MONO, color: color || 'text.primary' }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{sub}</Typography>
    </Box>
  );

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  return (
    <Stack spacing={2.5} className="fuzzscan-page" sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1.5}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Supply chain</Typography>
          <Typography color="text.secondary" variant="body2">Show that your scan files are the originals and nobody changed them</Typography>
        </Box>
        <Button variant="outlined" color="inherit" onClick={() => setSbom({ scan: '', name: '', json: SBOM_START })}>Import a software list</Button>
      </Stack>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {summary.signing_key === 'app_secret' && (
        <Alert severity="warning">Signing uses the main app secret. Set FUZZSCAN_ARTIFACT_SIGNING_SECRET so a leak of one does not break the other. Changing it later makes older signatures fail to match.</Alert>
      )}

      <Stack direction="row" gap={2} flexWrap="wrap">
        {stat('Protected', c.good, GREEN, `of ${rows.length} files`)}
        {stat('Signed only', c.signed, '#fcd34d', 'needs an attestation')}
        {stat('Not protected', c.none, c.none ? '#fcd34d' : undefined, 'one click to sign')}
        {stat('Software lists', summary.sbom_documents ?? 0, undefined, 'SBOM documents')}
      </Stack>

      <Box sx={{ ...panel, overflow: 'hidden' }}>
        <Typography sx={{ p: 2.2, fontWeight: 700 }}>Your files</Typography>
        {!list.length && <Typography color="text.secondary" sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>No files yet. Run a scan and its output will show up here.</Typography>}
        {list.map((r) => {
          const s = STAGES[stageOf(r)];
          const res = checks[r.id];
          return (
            <Box key={r.id} sx={{ borderTop: 1, borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" gap={2.5} flexWrap="wrap" sx={{ px: 2.8, py: 1.8 }}>
                <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
                  <Typography sx={{ fontFamily: MONO, fontWeight: 600, fontSize: 14.5 }} noWrap>{r.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.source_tool || 'File'} · {ago(r.created_at)}</Typography>
                </Box>
                <Stack direction="row" gap={2} sx={{ flex: '1 1 260px' }}>
                  <Step label="Signed" on={r.is_signed} /><Step label="Attested" on={r.is_attested} /><Step label="Checked" on={Boolean(res)} />
                </Stack>
                <Box sx={{ px: 1.4, py: 0.4, borderRadius: 99, bgcolor: alpha(s.color, 0.14), color: s.color, fontSize: 11.5, fontWeight: 700 }}>{s.label}</Box>
                <Box sx={{ minWidth: 140, textAlign: 'right' }}>{busy === r.id ? <CircularProgress size={20} /> : act(r)}</Box>
              </Stack>
              {res && (
                <Stack direction="row" gap={2} alignItems="center" sx={{ mx: 2.8, mb: 2, p: 2, borderRadius: 2, border: 1, borderColor: alpha(VERDICT_COLOR[res.kind], 0.45), bgcolor: alpha(VERDICT_COLOR[res.kind], 0.1) }}>
                  <Typography sx={{ fontSize: 26, color: VERDICT_COLOR[res.kind] }}>{res.kind === 'ok' ? '✓' : res.kind === 'bad' ? '✕' : '–'}</Typography>
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: VERDICT_COLOR[res.kind] }}>{res.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{res.text} Checked {ago(res.at)}.</Typography>
                  </Box>
                </Stack>
              )}
            </Box>
          );
        })}
      </Box>
      <Typography variant="body2" color="text.secondary">
        Signed means the platform fingerprinted the file. Attested adds a note of who built it and how. Checked means we compared the file with its fingerprint just now. These marks can only be checked by this platform.
      </Typography>

      {audit?.summary && (
        <Box sx={{ ...panel, p: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography sx={{ fontWeight: 700 }}>Activity log</Typography>
            <Box sx={{ px: 1.4, py: 0.4, borderRadius: 99, fontSize: 11.5, fontWeight: 700, bgcolor: alpha(audit.summary.broken_links ? '#ef4444' : GREEN, 0.14), color: audit.summary.broken_links ? '#ef4444' : GREEN }}>
              {audit.summary.broken_links ? `${audit.summary.broken_links} broken links` : 'Intact'}
            </Box>
          </Stack>
          <Typography variant="caption" color="text.secondary">Each entry is linked to the one before it, so an edit or removal would break the chain.</Typography>
          {(audit.results || []).slice(-8).reverse().map((e) => (
            <Stack key={e.id} direction="row" gap={2} sx={{ py: 1.1, borderTop: 1, borderColor: 'divider' }}>
              <Box sx={{ width: 9, height: 9, mt: 0.8, borderRadius: '50%', bgcolor: e.valid_link ? GREEN : '#ef4444', flex: 'none' }} />
              <Typography variant="body2" sx={{ flex: 1 }}>{String(e.action).replace(/_/g, ' ')}{e.resource_id ? ` · file ${e.resource_id}` : ''}</Typography>
              <Typography variant="caption" color="text.secondary">{e.created_at ? ago(e.created_at) : `#${e.chain_index}`}</Typography>
            </Stack>
          ))}
        </Box>
      )}

      <Dialog open={Boolean(sbom)} onClose={() => setSbom(null)} fullWidth maxWidth="sm">
        <DialogTitle>Import a software list</DialogTitle>
        <DialogContent>
          {sbom && (
            <Stack spacing={1.6} sx={{ pt: 1 }}>
              <TextField label="Scan ID" size="small" value={sbom.scan} onChange={(e) => setSbom({ ...sbom, scan: e.target.value })} />
              <TextField label="Name (optional)" size="small" value={sbom.name} onChange={(e) => setSbom({ ...sbom, name: e.target.value })} />
              <TextField label="CycloneDX JSON" multiline minRows={7} value={sbom.json} onChange={(e) => setSbom({ ...sbom, json: e.target.value })} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setSbom(null)}>Cancel</Button>
          <Button variant="contained" color="success" disabled={!sbom?.scan} onClick={importSbom}>Import</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
